import React from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';

interface AnimatedMenuButtonProps {
  isOpen: boolean;
  onClick: () => void;
  className?: string;
}

const AnimatedMenuButton: React.FC<AnimatedMenuButtonProps> = ({
  isOpen,
  onClick,
  className = ''
}) => {
  const buttonVariants = {
    closed: {
      scale: 1,
      rotate: 0,
      backgroundColor: 'transparent'
    },
    open: {
      scale: 1.1,
      rotate: 180,
      backgroundColor: 'var(--color-nav-bg-hover)'
    },
    hover: {
      scale: 1.05,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: {
      scale: 0.95
    }
  };

  const iconVariants = {
    closed: {
      rotate: 0,
      scale: 1
    },
    open: {
      rotate: 90,
      scale: 1.1
    }
  };

  const pulseVariants = {
    closed: {
      scale: 1,
      opacity: 0
    },
    open: {
      scale: [1, 1.5, 1],
      opacity: [0.3, 0.1, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div className="relative">
      {/* Pulse effect when open */}
      <motion.div
        className="absolute inset-0 rounded-lg bg-green-500"
        variants={pulseVariants}
        animate={isOpen ? 'open' : 'closed'}
      />
      
      {/* Main button */}
      <motion.button
        onClick={onClick}
        className={`inline-flex items-center justify-center p-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 min-h-[48px] min-w-[48px] relative z-10 ${className}`}
        variants={buttonVariants}
        initial="closed"
        animate={isOpen ? 'open' : 'closed'}
        whileHover="hover"
        whileTap="tap"
        style={{
          color: 'var(--color-nav-text)'
        }}
        aria-expanded={isOpen}
        aria-label="Toggle navigation menu"
        aria-controls="mobile-menu"
      >
        {/* Icon container with morphing animation */}
        <motion.div
          variants={iconVariants}
          animate={isOpen ? 'open' : 'closed'}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 20
          }}
        >
          <motion.div
            initial={false}
            animate={{
              rotate: isOpen ? 180 : 0,
              scale: isOpen ? 0.9 : 1
            }}
            transition={{
              duration: 0.3,
              ease: "easeInOut"
            }}
          >
            {isOpen ? (
              <motion.div
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
              >
                <X className="block h-6 w-6" aria-hidden="true" />
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, rotate: 90 }}
                animate={{ opacity: 1, rotate: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
              >
                <Menu className="block h-6 w-6" aria-hidden="true" />
              </motion.div>
            )}
          </motion.div>
        </motion.div>

        {/* Magnetic field effect */}
        <motion.div
          className="absolute inset-0 rounded-lg border-2 border-green-500/30"
          initial={{ scale: 0, opacity: 0 }}
          whileHover={{
            scale: 1.2,
            opacity: 0.5,
            transition: {
              duration: 0.3,
              ease: "easeOut"
            }
          }}
        />
      </motion.button>
    </motion.div>
  );
};

export default AnimatedMenuButton;
